import React, { useRef } from 'react'
import queryString from 'query-string'
import FlatPagination from 'material-ui-flat-pagination'
import axios from 'axios'
import moment from 'moment-timezone'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'
import { useHistory, useLocation } from 'react-router'
import store from '../../store'
import { setDialog } from '../../store/actions/dialog'
import { setAuth } from '../../store/actions/auth'
import {
  Grid,
  Paper,
  Box,
  Accordion,
  AccordionDetails,
  Typography,
  Chip,
  Button,
  FormControlLabel
} from '@material-ui/core'
import MuiAccordionSummary from '@material-ui/core/AccordionSummary'
import Title from '../atoms/Title'
import UserInfoTable from '../molecules/UserInfoTable'
import MessageBox from '../molecules/MessageBox'
import RightSideSubmitButton from '../molecules/RightSideSubmitButton'
import { User } from '../../types/User'
import { Admin } from '../../types/Admin'
import { Subject, Message } from '../../types/Message'
import { makeStyles, Theme } from '@material-ui/core/styles'
import ValidationAndLimitLengthInput from '../atoms/ValidationAndLimitLengthInput'
import TinyMCE from './TinyMCE'

const useStyle = makeStyles((theme: Theme) => ({
  root: { width: '100%' },
  paper: {
    padding: theme.spacing(2),
    display: 'flex',
    overflow: 'auto',
    flexDirection: 'column',
    height: `100%`
  },
  read: {
    border: 'none'
  },
  expandIcon: {
    transform: 'none !important',
    padding: '0',
    margin: '12px'
  }
}))

const useStyleLabel = makeStyles((theme: Theme) => ({
  root: {
    margin: 0,
    '& .MuiFormControlLabel-label': { visibility: 'hidden' }
  }
}))

type QueryString = {
  sort?: string
  order?: 'asc' | 'desc'
  page?: string
  limit?: string
}

type MessagesInSubject = Subject & {
  messages: Message[]
  unread: number
  user_unread: number
  latest_message_date: string
  notification_message: string
}

type FetchResponse = {
  number_of_hits: number
  rows: MessagesInSubject[]
  app_name: string
}

type Reply = { message: string; notificationMessage: string }

type Props = { kaiinNo: string; isModal?: boolean } & ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>

const MessageHistoryList: React.FC<Props> = React.memo(
  ({ kaiinNo, isModal, setDialog, admin, setAuth }) => {
    // ページをロードした瞬間の初期クエリストリング
    const history = useHistory()
    const location = useLocation()
    const initQuery: QueryString = React.useMemo(
      () => queryString.parse(location.search),
      [location.search]
    )
    const [total, setTotal] = React.useState<number>(0)
    const [limit, setLimit] = React.useState<number>(100)
    const [messagesInSubject, setMessagesInSubject] = React.useState<MessagesInSubject[]>([])
    const [user, setUser] = React.useState<User | null>()
    const [expanded, setExpanded] = React.useState<number | false>(false)
    const [reply, setReply] = React.useState<Reply>({
      message: '',
      notificationMessage: ''
    })
    const classes = useStyle({})
    const classesLabel = useStyleLabel({})
    const [validateState, setValidateState] = React.useState(false)
    const [defaultNotificationMessage, setDefaultNotificationMessage] = React.useState('')
    const editorRef = useRef(null)

    const initialSet = React.useCallback((data: FetchResponse) => {
      data.rows.forEach(subject => {
        const count = subject.messages.reduce((acc, current) => {
          if (!current.admin_read_flg) {
            acc = acc + 1
          }
          return acc
        }, 0)
        const count_user = subject.messages.reduce((acc, current) => {
          if (!current.user_read_flg) {
            acc = acc + 1
          }
          return acc
        }, 0)
        subject.unread = count
        subject.user_unread = count_user
      })
      setMessagesInSubject(data.rows)
      setTotal(data.number_of_hits)
      setLimit(data.number_of_hits > 0 ? 100 : 0)
      const _notificationMessage = `${data.app_name}から返信メッセージが届きました。`
      setDefaultNotificationMessage(_notificationMessage)
      setReply(prev => ({
        ...prev,
        notificationMessage: _notificationMessage
      }))
    }, [])

    const fetchMessages = React.useCallback(
      async (params: QueryString) => {
        if (kaiinNo) {
          const { data } = await axios.get<FetchResponse>(`/messages/kaiin/${kaiinNo}`, {
            params: {
              page: params.page,
              limit: limit
            }
          })
          initialSet(data)
        }
      },
      [kaiinNo, limit, initialSet]
    )

    const fetchAdmin = React.useCallback(async () => {
      const { data } = await axios.get<Admin>(`/admins/${admin.id}`)
      setAuth(admin.id, admin.token, admin.adminId, admin.password, data.name)
    }, [admin, setAuth])

    React.useEffect(() => {
      try {
        const fetchUser = async () => {
          const { data } = await axios.get<User>(`/users/kaiin/${kaiinNo}`)
          setUser(data)
        }
        // モーダルの初期表示時、呼び出し元画面urlクエリのpageがセットされるため初期値1をセット
        if (isModal) initQuery.page = '1'
        fetchMessages(initQuery)
        fetchUser()
      } catch (error) {
        alert(error.message)
      }
    }, [kaiinNo, initQuery, fetchMessages, isModal])

    const scrollPageTop = React.useCallback(
      () =>
        document.querySelector(isModal ? '.MuiDialogContent-root' : '#main-content').scroll({
          top: 0,
          left: 0
        }),
      [isModal]
    )

    const handlePageChange = React.useCallback(
      async (page: number) => {
        try {
          const query: QueryString = {
            page: String(page)
          }
          if (isModal) {
            initQuery.page = String(page)
          } else {
            history.push({
              pathname: `/message-history/${kaiinNo}`,
              search: queryString.stringify(query)
            })
          }
          setExpanded(false)
          scrollPageTop()
          await fetchMessages(query)
        } catch (error) {
          alert(error.message)
        }
      },
      [fetchMessages, history, kaiinNo, scrollPageTop, isModal, initQuery]
    )

    const currentPageCaption = React.useCallback(() => {
      const totalNumber = total
      const currentPage = Number(initQuery.page) || 1
      const totalPage = Math.ceil(total / limit)
      const startNumber = totalNumber === 0 ? 0 : (currentPage - 1) * limit + 1
      const lastNumber = currentPage === totalPage ? totalNumber : currentPage * limit
      return (
        <Typography variant="caption" align="center" display="block" color="primary">
          全 {totalNumber} 件｜ ({startNumber} - {lastNumber}件)
        </Typography>
      )
    }, [initQuery.page, limit, total])

    const handleExpansion = React.useCallback(
      async (subjectIndex: number, isExpanded: boolean) => {
        if (messagesInSubject[subjectIndex].unread > 0) {
          await axios.post(`/messages/read/${messagesInSubject[subjectIndex].id}`, {
            reader_type: 'admin'
          })
        }
        setExpanded(isExpanded ? subjectIndex : false)
        setReply({ message: '', notificationMessage: defaultNotificationMessage })
      },
      [messagesInSubject, defaultNotificationMessage]
    )

    const checkInputValue = (inputValue: { message: string; notificationMessage: string }) => {
      let countFalse = 0
      for (let v in inputValue) {
        if (!inputValue[v]) countFalse += 1
      }
      return countFalse === 0
    }
    const handleReplay = React.useCallback(
      async (reply: Reply, subjectId: number) => {
        if (!reply.message) {
          alert('メッセージを入力してください。')
          return
        }
        const { data } = await axios.get<Subject>(`/messages/subject/${subjectId}`)
        if (data.delete_at) {
          alert('ユーザーによって削除されているためメッセージを送信できません。')
          return
        }
        setDialog(
          '確認',
          'メッセージを送信します。よろしいですか？',
          async () => {
            try {
              fetchAdmin()
              await axios.post(`/messages/replay/${subjectId}`, {
                message: reply.message,
                sender: admin.name,
                sender_type: 'admin',
                notification_message: reply.notificationMessage
              })
              setReply({ message: '', notificationMessage: defaultNotificationMessage })
              if (!isModal) {
                handlePageChange(1)
                setExpanded(0)
              } else {
                await fetchMessages(initQuery)
                scrollPageTop()
                setExpanded(0)
              }
            } catch (error) {
              alert(error.message)
            }
          },
          () => {}
        )
      },
      [handlePageChange, isModal, fetchMessages, scrollPageTop, initQuery]
    )

    const handleDelete = React.useCallback(
      async (messageId: number, expanded: number | false) => {
        setDialog(
          '確認',
          '削除してもよろしいですか？ユーザー画面からも非表示になります。',
          async () => {
            try {
              const subjectsLength = messagesInSubject.length
              await axios.delete(`messages/${messageId}`)
              const { data } = await axios.get<FetchResponse>(`/messages/kaiin/${kaiinNo}`, {
                params: {
                  page: initQuery.page,
                  limit: limit
                }
              })
              initialSet(data)
              if (data.rows.length !== subjectsLength || data.number_of_hits !== total) {
                setExpanded(false)
              } else {
                setExpanded(expanded)
              }
            } catch (error) {
              alert(error.message)
            }
          },
          () => {}
        )
      },
      [initQuery.page, kaiinNo, limit, messagesInSubject.length, total, initialSet, setDialog]
    )

    const handleDeleteCancel = async (subjectIndex: number) => {
      setDialog(
        '確認',
        '削除を取り消してもよろしいですか？',
        async () => {
          handleCheckBoxChange(subjectIndex, 'delete_at', null)
        },
        () => {}
      )
    }

    const handleCheckBoxChange = async (subjectIndex: number, key: string, value: any) => {
      try {
        const { data } = await axios.put<Subject>(
          `/messages/subject/${messagesInSubject[subjectIndex].id}`,
          {
            [key]: value
          }
        )
        const messagesInSubjectCopy = messagesInSubject.slice()
        messagesInSubjectCopy[subjectIndex] = {
          ...messagesInSubject[subjectIndex],
          noreply_flg: data.noreply_flg,
          delete_at: data.delete_at
        }
        setMessagesInSubject(messagesInSubjectCopy)
      } catch (error) {
        alert(error.message)
      }
    }

    return (
      <React.Fragment>
        <Grid item xs={12}>
          <Paper className={classes.paper}>
            <Box display="flex" justifyContent="space-between">
              <div>
                <Title>メッセージ履歴</Title>
                <Typography variant="body1" paragraph>
                  ユーザーとのメッセージ履歴を表示します。
                </Typography>
              </div>
              {!isModal && (
                <Box ml={3}>
                  <Button color="primary" variant="outlined" href="/messages">
                    一覧に戻る
                  </Button>
                </Box>
              )}
            </Box>
            <div style={{ minWidth: 800 }}>
              <UserInfoTable
                users={user ? [user] : []}
                height={100}
                onDelete={false}
                displayKatudouStatus={true}
                displayKaiinEmail={false}
                handleCheckUsers={() => {}}
              />
            </div>
            <Box mb={7}>
              {messagesInSubject.length > 0 && (
                <React.Fragment>
                  <Box my={4}>
                    <Box display="flex" justifyContent="center">
                      {currentPageCaption}
                    </Box>
                    <Box display="flex" justifyContent="center">
                      <FlatPagination
                        limit={limit}
                        offset={(Number(initQuery.page || 1) - 1) * limit}
                        total={total}
                        onClick={(ev, offset, _page) => {
                          handlePageChange(_page)
                        }}
                      />
                    </Box>
                  </Box>
                  {messagesInSubject.map((subject, subjectIndex) => (
                    <Accordion
                      expanded={expanded === subjectIndex}
                      onChange={(event: React.ChangeEvent<{}>, expanded: boolean) => {
                        handleExpansion(subjectIndex, expanded)
                      }}
                      key={`reg-subjects-${subjectIndex}`}
                    >
                      <MuiAccordionSummary
                        aria-label="Expand"
                        expandIcon={
                          <Button variant="contained" color="primary">
                            {expanded === subjectIndex ? '閉じる' : '開く'}
                          </Button>
                        }
                        classes={{ expandIcon: classes.expandIcon }}
                      >
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          width="100%"
                        >
                          <Box flex="1" pr={2}>
                            <Box display="flex" alignItems="center" mb={2}>
                              <Box mr={2}>
                                <Chip
                                  size="small"
                                  label={`メッセージ数${subject.messages.length}件`}
                                  color="default"
                                />
                              </Box>
                              {subject.unread > 0 && (
                                <Box mr={2}>
                                  <Chip
                                    size="small"
                                    label={`未読${subject.unread}件`}
                                    color="secondary"
                                  />
                                </Box>
                              )}
                              {subject.delete_at && (
                                <Box minWidth="100px" maxWidth="385px">
                                  <Typography variant="caption" color="error">
                                    ※ユーザーによりメッセージが削除されました。
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                            <Box width="100%" display="flex" alignItems="center" ml={2}>
                              <Box width="30px" mr={2}>
                                <Typography variant="caption">件名: </Typography>
                              </Box>
                              <Box flex="1" pr="15px">
                                <Typography
                                  variant="subtitle2"
                                  style={{
                                    wordBreak: 'break-all'
                                  }}
                                >
                                  {subject.subject}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>

                          {subject.delete_at && (
                            <Box
                              mr="20px"
                              display="flex"
                              flexDirection="column"
                              alignItems="flex-end"
                            >
                              <FormControlLabel
                                aria-label="Acknowledge"
                                onClick={event => event.stopPropagation()}
                                control={
                                  <Button
                                    aria-label="Acknowledge"
                                    variant="contained"
                                    color="primary"
                                    onClick={() => handleDeleteCancel(subjectIndex)}
                                  >
                                    削除を取り消す
                                  </Button>
                                }
                                label=""
                                className={classesLabel.root}
                              />
                            </Box>
                          )}

                          <Box
                            mr="20px"
                            display="flex"
                            flexDirection="column"
                            alignItems="flex-end"
                          >
                            <FormControlLabel
                              aria-label="Acknowledge"
                              onClick={event => event.stopPropagation()}
                              control={
                                <Button
                                  aria-label="Acknowledge"
                                  variant="contained"
                                  color={!Boolean(subject.noreply_flg) ? 'primary' : 'default'}
                                  onClick={() =>
                                    handleCheckBoxChange(
                                      subjectIndex,
                                      'noreply_flg',
                                      !subject.noreply_flg
                                    )
                                  }
                                  style={{ width: 90 }}
                                  disabled={
                                    ((user && user.katudou_status != '1') || subject.delete_at) &&
                                    true
                                  }
                                >
                                  {user && user.katudou_status == '1'
                                    ? !Boolean(subject.noreply_flg)
                                      ? '受付中'
                                      : '受付停止中'
                                    : '退会者'}
                                </Button>
                              }
                              label=""
                              className={classesLabel.root}
                            />
                          </Box>

                          <Box
                            mr="20px"
                            display="flex"
                            flexDirection="column"
                            alignItems="flex-end"
                          >
                            {subject.user_unread > 0 ? (
                              <Chip
                                size="small"
                                label={`会員の未読あり`}
                                color="secondary"
                                variant="outlined"
                              />
                            ) : (
                              <Chip
                                size="small"
                                label={`会員の未読なし`}
                                color="default"
                                variant="outlined"
                                className={classes.read}
                              />
                            )}
                          </Box>

                          <Box
                            width="175px"
                            mr="3px"
                            display="flex"
                            flexDirection="column"
                            alignItems="flex-end"
                          >
                            <Typography variant="caption">
                              初回作成日時: {moment(subject.created_at).format('YYYY/MM/DD HH:mm')}
                            </Typography>
                            <Typography variant="caption">
                              最終投稿日時:{' '}
                              {moment(subject.latest_message_date).format('YYYY/MM/DD HH:mm')}
                            </Typography>
                            {subject.delete_at && (
                              <Typography variant="caption">
                                会員削除日時: {moment(subject.delete_at).format('YYYY/MM/DD HH:mm')}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </MuiAccordionSummary>
                      <AccordionDetails>
                        <Box width="100%">
                          {subject.messages.map((message, messageIndex) => (
                            <MessageBox
                              key={`reg-message-${subjectIndex}-${messageIndex}`}
                              message={message}
                              isLast={messageIndex + 1 === subject.messages.length}
                              deleteHandler={() => {
                                if (
                                  String(message.is_parent) === '1' &&
                                  subject.messages.length > 1
                                ) {
                                  alert('返信があるメッセージは削除できません。')
                                } else {
                                  handleDelete(message.id, expanded)
                                }
                              }}
                            />
                          ))}
                          {user && user.katudou_status == '1' ? (
                            subject.delete_at ? (
                              <Box ml={1} mb={1}>
                                <Typography variant="body1">
                                  ※ユーザーによりメッセージが削除されました。新規メッセージの送信には「削除を取り消す」ボタンを押してください。メッセージが再表示されます。
                                </Typography>
                              </Box>
                            ) : (
                              <Box>
                                <Box mb={2}>
                                  <Typography variant="subtitle2">プッシュ通知タイトル</Typography>
                                  <ValidationAndLimitLengthInput
                                    value={reply.notificationMessage}
                                    label=""
                                    required={false}
                                    variant="outlined"
                                    fullWidth
                                    inputHandler={input =>
                                      setReply(prev => ({
                                        ...prev,
                                        notificationMessage: input
                                      }))
                                    }
                                    invalidStr={/["'<>&]/}
                                    strLength={100}
                                    validationErrorMessage="使用できない記号が含まれています。"
                                    strLengthErrorMessage="文字数が100文字を超えています。文字数を調節してください。"
                                    validationStateChange={isFullFilled => {
                                      setValidateState(isFullFilled)
                                    }}
                                  />
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      color="error"
                                      style={{ marginLeft: '12px' }}
                                    >
                                      ※端末により40Byte以降の文字が表示されない場合もございます。
                                    </Typography>
                                    <br />
                                    <Typography
                                      variant="caption"
                                      color="error"
                                      style={{ marginLeft: '24px' }}
                                    >
                                      通知文字数が長くならないようご注意ください。
                                    </Typography>
                                    <br />
                                    <Typography
                                      variant="caption"
                                      color="error"
                                      style={{ marginLeft: '12px' }}
                                    >
                                      ※以下の記号類は、端末側で文字化けする可能性がありますので、使用しないでください。
                                    </Typography>
                                    <br />
                                    <Typography
                                      variant="caption"
                                      color="error"
                                      style={{ marginLeft: '24px' }}
                                    >
                                      「"」「&#39;」「&lt;」「&gt;」「&amp;」
                                    </Typography>
                                  </Box>
                                </Box>
                                <Box mb={2}>
                                  <Typography variant="subtitle2">メッセージ</Typography>
                                  <TinyMCE
                                    initialValue={reply.message}
                                    editorRef={editorRef}
                                    inputHandler={(tinyMCEContent: string) =>
                                      setReply(prev => ({ ...prev, message: tinyMCEContent }))
                                    }
                                  />
                                </Box>
                                <RightSideSubmitButton
                                  submittable={validateState && checkInputValue(reply)}
                                  resettable
                                  submitText={'送信'}
                                  submitHandler={() => {
                                    handleReplay(reply, subject.id)
                                  }}
                                  resetHandler={() => {
                                    setReply({
                                      message: '',
                                      notificationMessage: defaultNotificationMessage
                                    })
                                  }}
                                />
                              </Box>
                            )
                          ) : (
                            <Box ml={1} mb={1}>
                              <Typography variant="body1">
                                ※退会者の場合、新規メッセージの送信はできません。
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </React.Fragment>
              )}
              <Box my={4}>
                <Box display="flex" justifyContent="center">
                  {currentPageCaption}
                </Box>
                <Box display="flex" justifyContent="center">
                  <FlatPagination
                    limit={limit}
                    offset={(Number(initQuery.page || 1) - 1) * limit}
                    total={total}
                    onClick={(ev, offset, _page) => {
                      handlePageChange(_page)
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </React.Fragment>
    )
  }
)

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    setDialog: (title: string, content: string, okHandler: () => void, ngHandler: () => void) =>
      dispatch(setDialog(title, content, okHandler, ngHandler)),
    setAuth: (id: number, token: string, adminId: string, password: string, name: string) =>
      dispatch(setAuth(id, token, adminId, password, name))
  }
}

const mapStateToProps = (state: ReturnType<typeof store.getState>) => {
  return {
    admin: state.auth
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(MessageHistoryList)
